'use client';

import React from 'react';
import Image from 'next/image';
import { Carousel, CarouselItem, useCarousel } from '@/components/ui/design-system';
import { Card, CardContent } from '@/components/ui/design-system';
import { WaveShape } from '@/components/ui/WaveShape';
import { Heading, Text, Badge } from '@/components/ui/design-system';
import { cn } from '@/lib/utils';

// Enhanced case studies data with diverse packaging solutions
const caseStudies = [
  { 
    id: 1, 
    title: 'Premium Beverage Cans', 
    image: '/images/pages/home/case-studies/case-study-1.jpg', 
    alt: 'Premium aluminum beverage cans with vibrant branding and custom printing',
    category: 'Beverage',
    description: 'Custom aluminum cans with premium finishes for craft beverage brands'
  },
  { 
    id: 2, 
    title: 'Sustainable Food Containers', 
    image: '/images/pages/home/case-studies/case-study-2.jpg', 
    alt: 'Eco-friendly food packaging containers made from recyclable materials',
    category: 'Food & Beverage',
    description: 'Biodegradable packaging solutions for organic food products'
  },
  { 
    id: 3, 
    title: 'Luxury Cosmetic Jars', 
    image: '/images/pages/home/case-studies/case-study-3.jpg', 
    alt: 'High-end cosmetic jars with premium finishes and custom branding',
    category: 'Cosmetics',
    description: 'Premium glass jars with metallic accents for luxury skincare brands'
  },
  { 
    id: 4, 
    title: 'Pharmaceutical Bottles', 
    image: '/images/pages/home/case-studies/case-study-4.jpg', 
    alt: 'Medical-grade pharmaceutical bottles with safety seals and clear labeling',
    category: 'Healthcare',
    description: 'Child-resistant bottles with tamper-evident seals for pharmaceutical products'
  },
  { 
    id: 5, 
    title: 'Artisan Coffee Cans', 
    image: '/images/pages/home/case-studies/case-study-5.jpg', 
    alt: 'Premium coffee storage cans with airtight seals and custom artwork',
    category: 'Food & Beverage',
    description: 'Airtight metal cans preserving freshness for specialty coffee roasters'
  },
  { 
    id: 6, 
    title: 'Essential Oil Bottles', 
    image: '/images/pages/home/case-studies/case-study-6.jpg', 
    alt: 'Dark glass essential oil bottles with dropper caps and elegant labeling',
    category: 'Wellness',
    description: 'UV-protective glass bottles with precision dropper systems'
  },
  { 
    id: 7, 
    title: 'Gourmet Sauce Jars', 
    image: '/images/pages/home/case-studies/case-study-7.jpg', 
    alt: 'Premium glass jars for gourmet sauces with decorative lids and labels',
    category: 'Food & Beverage',
    description: 'Heat-resistant glass jars with custom embossed designs for artisan sauces'
  },
  { 
    id: 8, 
    title: 'Personal Care Tubes', 
    image: '/images/pages/home/case-studies/case-study-8.jpg', 
    alt: 'Sleek cosmetic tubes for lotions and creams with pump dispensers',
    category: 'Personal Care',
    description: 'Airless pump tubes maintaining product integrity for skincare formulations'
  },
  { 
    id: 9, 
    title: 'Supplement Containers', 
    image: '/images/pages/home/case-studies/case-study-9.jpg', 
    alt: 'Vitamin and supplement bottles with moisture-resistant seals',
    category: 'Health & Nutrition',
    description: 'Moisture-barrier bottles protecting nutritional supplements from degradation'
  },
  { 
    id: 10, 
    title: 'Craft Beer Bottles', 
    image: '/images/pages/home/case-studies/case-study-10.jpg', 
    alt: 'Custom glass beer bottles with unique shapes and branded labels',
    category: 'Beverage',
    description: 'Distinctive bottle shapes with embossed branding for craft breweries'
  },
  { 
    id: 11, 
    title: 'Luxury Perfume Bottles', 
    image: '/images/pages/home/case-studies/case-study-11.jpg', 
    alt: 'Elegant perfume bottles with crystal-like finishes and premium atomizers',
    category: 'Fragrance',
    description: 'Crystal-cut glass bottles with precision spray mechanisms for luxury fragrances'
  },
];  

export function CaseStudiesCarousel() {
  // Local slide component that can access Carousel context
  const Slide: React.FC<{
    study: (typeof caseStudies)[number];
    index: number;
    total: number;
  }> = ({ study, index, total }) => {
    const { selectedIndex } = useCarousel();
    const isActive = selectedIndex === index;

    return (
      <CarouselItem
        className="basis-full md:basis-1/3 lg:basis-1/5 px-2"
        slideIndex={index}
      >
        {/* Fixed outer container with consistent height - same as desktop */}
        <div className="h-[420px] flex items-center">
          <div 
            className={cn(
              "relative rounded-xl overflow-hidden transition-all duration-500 ease-in-out w-full",
              isActive 
                ? "h-full" // Full height when active
                : "h-[80%]" // Shorter height when not active
            )}
          >
            {/* Card image */}
            <Image
              src={study.image}
              alt={study.title}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 33vw, 20vw"
              loading="lazy"
            />
            
            {/* Keep title and description in the DOM for SEO but visually hidden */}
            <div className="sr-only">
              <h3>{study.title}</h3>
              <p>{study.description}</p>
            </div>
          </div>
        </div>
      </CarouselItem>
    );
  };

  // Auto-advance with looping enabled
  const AutoAdvance: React.FC<{ intervalMs?: number }> = ({ intervalMs = 3000 }) => {
    const { api } = useCarousel();

    React.useEffect(() => {
      if (!api) return;
      const id = window.setInterval(() => {
        api.scrollNext();
      }, intervalMs);
      return () => window.clearInterval(id);
    }, [api, intervalMs]);

    return null;
  };
  return (
    <section 
      className="relative w-full pt-12 md:pt-24 lg:pt-32 pb-12 md:pb-24 lg:pb-32"
      aria-labelledby="case-studies-heading"
      role="region"
    >
      {/* Background Image with Fixed Attachment */}
      <div 
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: 'url(/images/pages/home/case-studies/case-bg.png)',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      ></div>
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" aria-hidden="true" />
      
      <style jsx global>{`
        .case-studies-carousel-spacing .carousel-dots {
          margin-top: 3rem; /* 48px spacing between cards and dots */
        }
        
        @media (min-width: 1024px) {
          .case-studies-carousel-spacing .carousel-arrow {
            display: flex;
          }
        }
        
        @media (max-width: 640px) {
          /* Ensure mobile shows single centered image with desktop dimensions */
          .case-studies-carousel-spacing [data-state="active"] {
            transform: scale(1) !important;
            opacity: 1 !important;
          }
          
          .case-studies-carousel-spacing [data-state="inactive"] {
            transform: scale(0.6) !important;
            opacity: 0.3 !important;
          }
        }
        
        @media (min-width: 641px) and (max-width: 1023px) {
          /* Tablet view - show 3 images with desktop sizing */
          .case-studies-carousel-spacing [data-state="active"] {
            transform: scale(1) !important;
            opacity: 1 !important;
          }
          
          .case-studies-carousel-spacing [data-state="inactive"] {
            transform: scale(0.85) !important;
            opacity: 0.8 !important;
          }
        }
        
        /* Fix carousel arrow colors to match theme instead of orange */
        .case-studies-carousel-spacing .carousel-arrow {
          background-color: hsl(var(--background)) !important;
          border-color: hsl(var(--border)) !important;
          color: hsl(var(--foreground)) !important;
          transition: all 0.2s ease-in-out !important;
        }
        
        .case-studies-carousel-spacing .carousel-arrow:hover:not(:disabled) {
          background-color: hsl(var(--primary)) !important;
          border-color: hsl(var(--primary)) !important;
          color: hsl(var(--primary-foreground)) !important;
          transform: translateY(-50%) scale(1.05) !important;
        }
        
        .case-studies-carousel-spacing .carousel-arrow:disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
        }
        
        /* Dark mode specific overrides */
        .dark .case-studies-carousel-spacing .carousel-arrow {
          background-color: hsl(var(--background)) !important;
          border-color: hsl(var(--border)) !important;
          color: hsl(var(--foreground)) !important;
        }
        
        .dark .case-studies-carousel-spacing .carousel-arrow:hover:not(:disabled) {
          background-color: hsl(var(--primary)) !important;
          border-color: hsl(var(--primary)) !important;
          color: hsl(var(--primary-foreground)) !important;
        }
      `}</style>

      <div className="relative z-10 container mx-auto px-4">
        <div className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
          <Badge variant="premium" size="lg" className="mb-4">
            Success Stories
          </Badge>
          <Heading 
            level={2} 
            id="case-studies-heading"
            className="text-4xl font-bold leading-tight mb-4"
          >
            Case Studies That Define Our Capabilities
          </Heading>
          <Text 
            size="xl" 
            className="text-muted-foreground max-w-3xl mx-auto"
          >
            Explore our success stories and see how we've helped brands like yours achieve their packaging goals.
          </Text>
        </div>
        {/* Fixed responsive height container with balanced padding - same as desktop */}
        <div className="w-full h-[480px] transition-all duration-300 overflow-hidden">
          <div className="-mx-4 pl-4 md:pl-6 lg:pl-8"> {/* Negative margin with right shift */}
            <Carousel
              options={{
                align: 'center',
                loop: true,
                startIndex: Math.floor(caseStudies.length / 2),
                containScroll: 'trimSnaps' as 'trimSnaps',
                slidesToScroll: 1,
              }}
              autoScroll={false}
              showArrows={true}
              showDots={true}
              className="w-full case-studies-carousel-spacing h-full px-4"
              aria-label="Case studies carousel showcasing our packaging solutions"
            >
              {caseStudies.map((study, index) => (
                <Slide key={study.id} study={study} index={index} total={caseStudies.length} />
              ))}
              {/* Auto-advance with looping */}
              <AutoAdvance intervalMs={3000} />
            </Carousel>
          </div>
        </div>
      </div>
      <WaveShape position="bottom" className="-bottom-1 relative z-10" />
    </section>
  );
}
